import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Checkbox, CircularProgress, FormControl, FormControlLabel,
  FormHelperText, Grid, InputLabel, MenuItem, Paper, Select, TextField,
  Typography,
} from '@mui/material';
import TuneIcon from '@mui/icons-material/Tune';
import { GET_CUSTOM_FIELDS } from '../../api';

export const validateCustomFieldValues = (definitions, values = {}) => {
  const errors = {};
  (definitions || []).filter((item) => item.is_active && item.can_write).forEach((item) => {
    const value = values?.[item.field_name];
    if (item.is_required && (value === undefined || value === null || value === '')) {
      errors[item.field_name] = 'This field is required.';
      return;
    }
    if (value === undefined || value === null || value === '') return;
    if (item.field_type === 'NUMBER') {
      const number = Number(value);
      if (!Number.isFinite(number)) {
        errors[item.field_name] = 'Enter a valid number.';
      } else if (item.minimum_value !== null && item.minimum_value !== undefined
        && number < Number(item.minimum_value)) {
        errors[item.field_name] = `Enter a value of at least ${Number(item.minimum_value)}.`;
      }
    }
    if (item.field_type === 'CHOICE' && !(item.choices_json || []).includes(value)) {
      errors[item.field_name] = 'Choose one of the configured values.';
    }
  });
  return errors;
};

export const useCustomFieldDefinitions = () => {
  const [definitions, setDefinitions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const reload = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await GET_CUSTOM_FIELDS();
      setDefinitions(Array.isArray(data) ? data : []);
    } catch (requestError) {
      setError(
        requestError?.response?.data?.detail
        || requestError?.response?.data?.error
        || 'Unable to load custom employee fields.'
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);
  return { definitions, loading, error, reload };
};

const CustomFieldsSection = ({
  definitions = [], loading = false, error = '', values = {}, onChange,
  title = 'Additional Employee Details', readOnly = false,
}) => {
  const visible = useMemo(() => definitions.filter(
    (item) => item.is_active && (item.can_read || item.can_write)
  ), [definitions]);
  const validationErrors = validateCustomFieldValues(definitions, values);

  const update = (definition, rawValue) => {
    if (!onChange || readOnly || !definition.can_write) return;
    let value = rawValue;
    if (definition.field_type === 'NUMBER') {
      value = rawValue === '' ? '' : Number(rawValue);
    }
    onChange({ ...values, [definition.field_name]: value });
  };

  const field = (definition) => {
    const canRead = Boolean(definition.can_read);
    const disabled = readOnly || !definition.can_write;
    const value = canRead ? (values?.[definition.field_name] ?? '') : '';
    const common = {
      fullWidth: true,
      disabled,
      required: definition.is_required,
      error: Boolean(validationErrors[definition.field_name]),
      helperText: validationErrors[definition.field_name]
        || (!canRead && definition.can_write ? 'Existing value is restricted; entering a value will replace it.' : ''),
    };

    if (definition.field_type === 'BOOLEAN') {
      return (
        <FormControl error={Boolean(validationErrors[definition.field_name])} disabled={disabled}>
          <FormControlLabel
            control={(
              <Checkbox
                checked={value === true}
                onChange={(event) => update(definition, event.target.checked)}
              />
            )}
            label={`${definition.field_label}${definition.is_required ? ' *' : ''}`}
          />
          {validationErrors[definition.field_name]
            && <FormHelperText>{validationErrors[definition.field_name]}</FormHelperText>}
        </FormControl>
      );
    }
    if (definition.field_type === 'CHOICE') {
      return (
        <FormControl
          fullWidth
          disabled={disabled}
          required={definition.is_required}
          error={Boolean(validationErrors[definition.field_name])}
        >
          <InputLabel>{definition.field_label}</InputLabel>
          <Select
            value={value}
            label={definition.field_label}
            onChange={(event) => update(definition, event.target.value)}
          >
            <MenuItem value=""><em>Not selected</em></MenuItem>
            {(definition.choices_json || []).map((choice) => (
              <MenuItem key={choice} value={choice}>{choice}</MenuItem>
            ))}
          </Select>
          {common.helperText && <FormHelperText>{common.helperText}</FormHelperText>}
        </FormControl>
      );
    }
    return (
      <TextField
        {...common}
        label={definition.field_label}
        type={definition.field_type === 'DATE'
          ? 'date' : definition.field_type === 'NUMBER' ? 'number' : 'text'}
        value={value}
        onChange={(event) => update(definition, event.target.value)}
        InputLabelProps={definition.field_type === 'DATE' ? { shrink: true } : undefined}
        inputProps={definition.field_type === 'NUMBER' ? {
          step: 'any',
          min: definition.minimum_value ?? undefined,
        } : undefined}
      />
    );
  };

  return (
    <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 }, border: '1px solid #eef0f4', borderRadius: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2.5 }}>
        <TuneIcon color="primary" />
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>{title}</Typography>
          <Typography variant="body2" color="text.secondary">
            Company-specific employee information.
          </Typography>
        </Box>
      </Box>
      {loading && <Box sx={{ py: 3, textAlign: 'center' }}><CircularProgress size={28} /></Box>}
      {!loading && error && <Alert severity="error">{error}</Alert>}
      {!loading && !error && visible.length === 0 && (
        <Alert severity="info">No custom employee fields are configured for this company.</Alert>
      )}
      {!loading && !error && visible.length > 0 && (
        <Grid container spacing={2.5}>
          {visible.map((definition) => (
            <Grid item xs={12} sm={6} key={definition.id || definition.field_name}>
              {field(definition)}
            </Grid>
          ))}
        </Grid>
      )}
    </Paper>
  );
};

export default CustomFieldsSection;
